// auth-setup.ts — written by generateOpenAPITsClient alongside each generated package.
// Not auto-generated by @hey-api/openapi-ts; hand-maintained in lib/openapi_templates/hey-api/.
//
// Provides scoped auth storage + axios interceptors for a single service's
// generated @hey-api/client-axios client. Each service has its own isolated
// storage keys (derived from SERVICE_ID) — no sharing with other services.
//
//   import { configureAuthInterceptors } from '@next-qr/openapi-client-chat/auth-setup';

import type { Client } from '@hey-api/client-axios';

/** Custom event dispatched on `window` after a token refresh (web only). */
export const TOKEN_REFRESHED_EVENT = 'next-qr:token-refreshed' as const;

// ── Generation-time constants ─────────────────────────────────────────────────
// These three values are substituted by generateOpenAPITsClient at codegen time.

/** This client's service id (e.g. "chat", "user", "organization"). */
export const SERVICE_ID = "mini-app-store";

/** Fallback base URL when nothing is persisted to storage. */
export const DEFAULT_API_BASE_URL = "http://localhost:18080";

/**
 * Where to POST `{ refresh_token }` on 401 to obtain a new access+refresh pair.
 * - Absolute URL (`http://…`) → used as-is.
 * - Relative path (`/…`)      → joined with the effective base URL at request time.
 * - Empty string              → no auto-refresh; 401 bubbles up for the UI to re-prompt login.
 */
export const REFRESH_ENDPOINT = "/api/user/auth/refresh";

// ── Scoped storage keys ───────────────────────────────────────────────────────
// Every service gets its own isolated scope. No cross-service sharing.

export const STORAGE_KEYS = {
  token:        `@next-qr:${SERVICE_ID}:access_token`,
  refreshToken: `@next-qr:${SERVICE_ID}:refresh_token`,
  user:         `@next-qr:${SERVICE_ID}:user`,
  baseUrl:      `@next-qr:${SERVICE_ID}:api_base`,
} as const;

export const STORAGE_STORE_KEYS = {
  token:        `@next-qr:mini-app-store:access_token`,
  refreshToken: `@next-qr:mini-app-store:refresh_token`,
  user:         `@next-qr:mini-app-store:user`,
  baseUrl:      `@next-qr:mini-app-store:api_base`,
} as const;

export type StorageKeys = typeof STORAGE_KEYS;

// ── Platform-agnostic storage adapter ─────────────────────────────────────────

export interface StorageAdapter {
  getItem(key: string): Promise<string | null> | string | null;
  setItem(key: string, value: string): Promise<void> | void;
  removeItem(key: string): Promise<void> | void;
}

/**
 * Picks the right storage adapter for the current runtime.
 *   - Web:          localStorage
 *   - React Native: globalThis.AsyncStorage (exposed by app/mobile/src/sharedModules.ts)
 */
export function getStorage(): StorageAdapter {
  if (typeof window !== 'undefined' && window.localStorage) {
    return {
      getItem:    (k) => localStorage.getItem(k),
      setItem:    (k, v) => localStorage.setItem(k, v),
      removeItem: (k) => localStorage.removeItem(k),
    };
  }
  const AS = (globalThis as any).AsyncStorage as {
    getItem(k: string): Promise<string | null>;
    setItem(k: string, v: string): Promise<void>;
    removeItem(k: string): Promise<void>;
  } | undefined;
  if (AS) {
    return {
      getItem:    (k) => AS.getItem(k),
      setItem:    (k, v) => AS.setItem(k, v),
      removeItem: (k) => AS.removeItem(k),
    };
  }
  throw new Error('[auth-setup] No suitable storage adapter found');
}

// ── Typed storage helpers (used by config.ts and callers) ─────────────────────

export interface AuthData {
  token: string;
  refresh_token: string;
  user?: Record<string, unknown>;
}

export async function storeAuthData(data: AuthData, storage: StorageAdapter = getStorage()): Promise<void> {
  await Promise.all([
    storage.setItem(STORAGE_KEYS.token, data.token),
    storage.setItem(STORAGE_KEYS.refreshToken, data.refresh_token),
    data.user ? storage.setItem(STORAGE_KEYS.user, JSON.stringify(data.user)) : Promise.resolve(),
  ]);
}

export async function clearAuthData(storage: StorageAdapter = getStorage()): Promise<void> {
  await Promise.all([
    storage.removeItem(STORAGE_KEYS.token),
    storage.removeItem(STORAGE_KEYS.refreshToken),
    storage.removeItem(STORAGE_KEYS.user),
  ]);
}

/**
 * Token lookup priority (mirrors `readStoredBaseURL`):
 *   1. Service's own scope if set.
 *   2. Super-app root (`@next-qr:mini-app-store:access_token`) — lets services
 *      inherit the super-app's session until they do their own login. Skipped
 *      when SERVICE_ID is mini-app-store.
 */
export async function readStoredToken(storage: StorageAdapter = getStorage()): Promise<string | null> {
  let own = await storage.getItem(STORAGE_KEYS.token);
  if (own) return own;
  if (!own) {
    own = await storage.getItem(STORAGE_STORE_KEYS.token);
  } 
  return own || null;
}

export async function readStoredRefreshToken(storage: StorageAdapter = getStorage()): Promise<string | null> {
  let own = await storage.getItem(STORAGE_KEYS.refreshToken);
  if (own) return own;
  if (!own) {
    own = await storage.getItem(STORAGE_STORE_KEYS.refreshToken);
  } 
  return own || null;
}

export async function readStoredUser<T extends Record<string, unknown> = Record<string, unknown>>(
  storage: StorageAdapter = getStorage(),
): Promise<T | null> {
  let raw = await storage.getItem(STORAGE_KEYS.user);
  if (!raw) {
    raw = await storage.getItem(STORAGE_STORE_KEYS.user);
  }
  try { return raw ? (JSON.parse(raw) as T) : null; } catch { return null; }
}

const isValidUrl = (own: string | null) => typeof own === 'string' && own.startsWith('http');

/**
 * Resolve the base URL for this service.
 *   1. Service's own scope (`@next-qr:{service}:api_base`) if set.
 *   2. Super-app root (`@next-qr:mini-app-store:api_base`) — default for services
 *      hosted alongside the mini-app-store. Skipped when the current service
 *      IS mini-app-store.
 *   3. Generation-time `DEFAULT_API_BASE_URL`.
 */
export async function readStoredBaseURL(storage: StorageAdapter = getStorage()): Promise<string> {
  let own = await storage.getItem(STORAGE_KEYS.baseUrl);
  if (!isValidUrl(own)) {
    own = await storage.getItem(STORAGE_STORE_KEYS.baseUrl) || '';
  }
  if (isValidUrl(own)) {
    return own || DEFAULT_API_BASE_URL;
  }
  return DEFAULT_API_BASE_URL;
}

// ── Interceptor setup ─────────────────────────────────────────────────────────

export interface AuthInterceptorOptions {
  /** Override the default storage adapter (picked from window/globalThis by default). */
  storage?: StorageAdapter;
  /** Called after auth is fully lost (no refresh token or refresh call failed). */
  onAuthLost?(): void;
}

/**
 * Attach Bearer-token + base-URL injection (and, if `REFRESH_ENDPOINT` is set,
 * automatic token refresh with queued retry) to the given hey-api client's
 * underlying axios instance. Call once at module init.
 *
 * IMPORTANT: Uses explicit Promise chaining (not async/await) because
 * `async` interceptor bodies are silently skipped inside mini-app bundles
 * eval'd via `new Function()` (see CLAUDE.md).
 */
export function configureAuthInterceptors(
  heyApiClient: Client,
  options: AuthInterceptorOptions = {},
): void {
  const storage = options.storage ?? getStorage();
  const axiosInstance = heyApiClient.instance;

  // ── Request: inject Bearer token + dynamic base URL ───────────────────────
  axiosInstance.interceptors.request.use((config) => {
    return Promise.resolve(readStoredBaseURL(storage))
      .then((baseURL) => {
        if (baseURL) config.baseURL = baseURL;
        return readStoredToken(storage);
      })
      .then((token) => {
        if (token) {
          const h = config.headers as any;
          if (h && typeof h.set === 'function') h.set('Authorization', `Bearer ${token}`);
          else {
            config.headers = config.headers ?? ({} as typeof config.headers);
            (config.headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
          }
        }
        return config;
      });
  });

  if (!REFRESH_ENDPOINT) return; // no auto-refresh configured

  // ── Response: handle 401 with queued refresh ──────────────────────────────
  let isRefreshing = false;
  let queue: Array<(token: string | null) => void> = [];
  function flush(token: string | null) {
    queue.forEach((cb) => cb(token));
    queue = [];
  }

  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      const original = error?.config;
      if (error?.response?.status !== 401 || !original || original._retried) {
        return Promise.reject(error);
      }
      original._retried = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          queue.push((token) => {
            if (!token) return reject(error);
            const h = original.headers;
            if (h && typeof h.set === 'function') h.set('Authorization', `Bearer ${token}`);
            else (h as Record<string, string>)['Authorization'] = `Bearer ${token}`;
            resolve(axiosInstance.request(original));
          });
        });
      }

      isRefreshing = true;

      return Promise.resolve(readStoredRefreshToken(storage))
        .then((rt) => {
          if (!rt) {
            return Promise.resolve(clearAuthData(storage)).then(() => {
              options.onAuthLost?.();
              flush(null);
              isRefreshing = false;
              return Promise.reject(error);
            });
          }
          return Promise.resolve(readStoredBaseURL(storage))
            .then((baseURL) => {
              const url = REFRESH_ENDPOINT.startsWith('http')
                ? REFRESH_ENDPOINT
                : `${baseURL}${REFRESH_ENDPOINT}`;
              return fetch(url, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ refresh_token: rt }),
              });
            })
            .then((res) => res.ok ? res.json() : null)
            .catch(() => null)
            .then((data: any) => {
              isRefreshing = false;
              if (!data?.token || !data?.refresh_token) {
                return Promise.resolve(clearAuthData(storage)).then(() => {
                  options.onAuthLost?.();
                  flush(null);
                  return Promise.reject(error);
                });
              }
              return Promise.resolve(storeAuthData({ token: data.token, refresh_token: data.refresh_token }, storage))
                .then(() => {
                  flush(data.token);
                  if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
                    try { window.dispatchEvent(new CustomEvent(TOKEN_REFRESHED_EVENT)); } catch { /* RN: no CustomEvent */ }
                  }
                  const h = original.headers;
                  if (h && typeof h.set === 'function') h.set('Authorization', `Bearer ${data.token}`);
                  else (h as Record<string, string>)['Authorization'] = `Bearer ${data.token}`;
                  return axiosInstance.request(original);
                });
            });
        });
    },
  );
}
